import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Calendar, Clock, Trophy, HelpCircle, AlertTriangle, Flag, ChevronDown, ChevronUp } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { logger } from "../lib/logger";
import { useI18n } from "../i18n";
import { useAuth } from "../contexts/AuthContext";

const POLL_MS = 60_000;

/* Detect pool id from /pools/:id/... route. */
function usePoolIdFromUrl() {
    const { pathname } = useLocation();
    return useMemo(() => {
        const m = pathname.match(/^\/pools\/([^/]+)/);
        return m ? m[1] : null;
    }, [pathname]);
}

/* Compact human time-delta — "in 2h 15m" / "in 3d 4h" / "in 12m". */
function formatDelta(toIso, t) {
    if (!toIso) return null;
    const target = new Date(toIso).getTime();
    const now = Date.now();
    const diff = target - now;
    if (diff <= 0) {
        return { label: t("status.now_live"), past: true, ms: diff };
    }
    const seconds = Math.floor(diff / 1000);
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    let label;
    if (days >= 2) label = `${days}d ${hours}h`;
    else if (days >= 1) label = `${days}d ${hours}h`;
    else if (hours >= 1) label = `${hours}h ${minutes}m`;
    else label = `${Math.max(minutes, 1)}m`;
    return { label, past: false, ms: diff };
}

export default function HeaderStatusBar() {
    const { user } = useAuth();
    const { t } = useI18n();
    const poolId = usePoolIdFromUrl();
    const [data, setData] = useState(null);
    const [now, setNow] = useState(Date.now());
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem("vmtips_statusbar_collapsed") === "1"; }
        catch { return false; }
    });

    const reload = useCallback(async () => {
        if (!user) return;
        try {
            const url = poolId ? `/status/header?pool_id=${encodeURIComponent(poolId)}` : "/status/header";
            const { data: payload } = await api.get(url);
            setData(payload);
        } catch (e) {
            logger.warn("header status failed", formatApiError(e?.response?.data?.detail) || e?.message);
        }
    }, [user, poolId]);

    // Initial load + poll + reload on route change
    useEffect(() => {
        if (!user) {
            setData(null);
            return;
        }
        reload();
        const id = setInterval(reload, POLL_MS);
        return () => clearInterval(id);
    }, [reload, user]);

    // Re-render every 30s so countdown labels stay fresh between API polls
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 30_000);
        return () => clearInterval(id);
    }, []);
    // Reference `now` so the lint understands why this re-render exists
    void now;

    const toggle = useCallback(() => {
        setCollapsed((prev) => {
            const next = !prev;
            try { localStorage.setItem("vmtips_statusbar_collapsed", next ? "1" : "0"); }
            catch (e) { logger.warn("statusbar collapse persist failed", e); }
            return next;
        });
    }, []);

    if (!user) return null;
    if (!data) {
        return (
            <div className="border-b border-[var(--border-base)] bg-[var(--bg-card)]/60 backdrop-blur-sm">
                <div className="max-w-6xl mx-auto px-4 py-1.5 text-[10px] uppercase tracking-widest text-[var(--text-muted)]">
                    ·
                </div>
            </div>
        );
    }

    const nextDelta = formatDelta(data.next_match?.kickoff, t);
    const finalDelta = formatDelta(data.final_match?.kickoff, t);
    const pool = data.pool;

    return (
        <div className="border-b border-[var(--border-base)] bg-[var(--bg-card)]/80 backdrop-blur-sm" data-testid="header-status-bar">
            <div className="max-w-6xl mx-auto px-3 sm:px-4">
                {/* compact bar — pills hide when collapsed, toggle stays */}
                <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto py-1.5 text-xs">
                    {!collapsed && (
                        <>
                            {/* matches played */}
                            <StatusPill testid="status-matches" icon={Calendar} title={t("status.matches", { played: data.matches_played, total: data.matches_total })}>
                                <span className="font-bold tabular-nums">{data.matches_played}</span>
                                <span className="text-[var(--text-muted)]">/{data.matches_total}</span>
                            </StatusPill>

                            {/* next kick-off */}
                            {data.next_match && nextDelta && (
                                <Link to={`/matches/${data.next_match.id}`}>
                                    <StatusPill testid="status-next" icon={Clock} title={`${t("status.next")} · ${data.next_match.round || ""}`}>
                                        <span className="text-[var(--text-muted)] hidden sm:inline">{t("status.next_short")}</span>
                                        <span className="hidden md:inline truncate max-w-[110px]">
                                            {data.next_match.home_team_name || "?"} – {data.next_match.away_team_name || "?"}
                                        </span>
                                        <span className={`font-bold tabular-nums ${nextDelta.past ? "text-red-400" : "text-[var(--accent-hex)]"}`}>
                                            {nextDelta.past ? nextDelta.label : `${t("status.in")} ${nextDelta.label}`}
                                        </span>
                                    </StatusPill>
                                </Link>
                            )}

                            {/* final */}
                            {data.final_match && finalDelta && (
                                <StatusPill testid="status-final" icon={Flag} title={t("status.final")}>
                                    <span className="text-[var(--text-muted)]">{t("status.final")}</span>
                                    <span className={`font-bold tabular-nums ${finalDelta.past ? "text-red-400" : "text-[var(--text-primary)]"}`}>
                                        {finalDelta.past ? t("status.over") : `${t("status.in")} ${finalDelta.label}`}
                                    </span>
                                </StatusPill>
                            )}

                            {/* pool-specific (when in a pool route) */}
                            {pool && (
                                <>
                                    <Top3Pill top={pool.top_three} t={t} poolId={pool.pool_id} />
                                    {pool.bonus_missing > 0 && (
                                        <Link to={`/pools/${pool.pool_id}`} state={{ tab: "bonus" }}
                                            title={pool.bonus_missing === 1
                                                ? t("status.bonus.missing_one", { n: pool.bonus_missing })
                                                : t("status.bonus.missing_many", { n: pool.bonus_missing })}>
                                            <StatusPill testid="status-bonus-missing" icon={HelpCircle} accent="amber">
                                                <span className="font-bold tabular-nums">{pool.bonus_missing}</span>
                                                <span className="hidden sm:inline text-amber-400">{" bonus"}</span>
                                            </StatusPill>
                                        </Link>
                                    )}
                                    {pool.has_bet && !pool.my_paid && (
                                        <Link to={`/pools/${pool.pool_id}`} state={{ tab: "bet" }}
                                            title={t("status.unpaid_tip")}>
                                            <StatusPill testid="status-unpaid" icon={AlertTriangle} accent="red">
                                                <span className="hidden sm:inline font-bold uppercase tracking-widest text-[10px]">{t("status.unpaid_tip")}</span>
                                            </StatusPill>
                                        </Link>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {collapsed && (
                        <span className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-bold" data-testid="status-collapsed-label">
                            {t("status.toggle_show")}
                        </span>
                    )}

                    <div className="ml-auto flex-shrink-0">
                        <button onClick={toggle}
                            title={collapsed ? t("status.toggle_show") : t("status.toggle_hide")}
                            aria-label={collapsed ? t("status.toggle_show") : t("status.toggle_hide")}
                            aria-expanded={!collapsed}
                            data-testid="status-toggle"
                            className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
                            {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function StatusPill({ icon: Icon, children, title, accent, testid }) {
    const tone = accent === "red"
        ? "border-red-500/40 text-red-400 bg-red-500/10"
        : accent === "amber"
            ? "border-amber-500/40 text-amber-400 bg-amber-500/10"
            : "border-[var(--border-base)] text-[var(--text-primary)] bg-[var(--bg-base)]";
    return (
        <div className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2 py-1 rounded border ${tone}`} title={title} data-testid={testid}>
            {Icon && <Icon className="h-3.5 w-3.5 flex-shrink-0" />}
            <div className="inline-flex items-center gap-1.5 whitespace-nowrap">{children}</div>
        </div>
    );
}

function Top3Pill({ top, t, poolId }) {
    if (!top || top.length === 0) {
        return (
            <StatusPill testid="status-top3-empty" icon={Trophy}>
                <span className="text-[var(--text-muted)]">{t("status.top3")}</span>
                <span className="text-[var(--text-muted)] italic">—</span>
            </StatusPill>
        );
    }
    return (
        <Link to={`/pools/${poolId}`} state={{ tab: "leaderboard" }}
            title={top.map((r) => `${r.rank}. ${r.user_name} (${r.total_points})`).join(" · ")}>
            <StatusPill testid="status-top3" icon={Trophy}>
                <span className="text-[var(--text-muted)] hidden sm:inline">{t("status.top3")}</span>
                <span className="inline-flex items-center gap-1.5">
                    {top.slice(0, 3).map((r, idx) => (
                        <span key={r.user_id} className="inline-flex items-center gap-0.5">
                            {idx > 0 && <span className="text-[var(--text-muted)]">·</span>}
                            <span className="text-[var(--accent-hex)] font-bold">{r.rank}</span>
                            <span className="text-[var(--text-primary)] max-w-[80px] truncate">{r.user_name}</span>
                            <span className="text-[var(--text-muted)] tabular-nums hidden sm:inline">({r.total_points})</span>
                        </span>
                    ))}
                </span>
            </StatusPill>
        </Link>
    );
}
